/**
 * Sophie Marea CRO - C-02 Maison custom-measurement validation.
 * Draft theme 159281873054 only.
 *
 * Problem this solves: the Maison measurement fields are rendered by the Globo Product Options app
 * inside its own container (`.gpo-app` / `.gpo-container`; the legacy `.gpo-product-variants` node may
 * be present but empty), which sits OUTSIDE the theme's product <form>. They carry no
 * `required`, `min` or `max`, so `form.checkValidity()` never sees them. Selecting Size = Custom and
 * leaving every measurement blank added the item to the cart with no error at all.
 *
 * Journey is CHECKOUT-FIRST: once the entry is valid the normal add-to-cart flow continues untouched.
 *
 * Why the listener is on `window` and not `document`: the theme's component.js registers ONE
 * document-level, capture-phase listener per event type and dispatches from there. In the capture
 * phase `window` is reached before `document`, so this handler always runs first regardless of script
 * load order, and stopImmediatePropagation() reliably prevents the theme's handler from submitting.
 *
 * No measurement RANGES are enforced - none exist in authoritative store content. Only: required
 * presence, valid numeric input, and a positive value. "If applicable" fields stay optional but are
 * still checked for validity when the customer fills them in.
 */
(function () {
  'use strict';

  var CONFIG_SELECTOR = '[data-sm-maison-validation]';
  // Globo has more than one possible mount point and the legacy `.gpo-product-variants` node can be
  // present but EMPTY. Resolve the container that actually holds the measurement controls.
  var GPO_SELECTOR = '.gpo-app, .gpo-container, .gpo-product-variants';

  function config() {
    return document.querySelector(CONFIG_SELECTOR);
  }

  function gpoPresent() {
    return !!document.querySelector(GPO_SELECTOR);
  }

  function gpoRoot() {
    var candidates = document.querySelectorAll(GPO_SELECTOR);
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].querySelector('input, select, textarea')) return candidates[i];
    }
    return null;
  }

  function list(el, attr) {
    var raw = (el && el.getAttribute(attr)) || '';
    return raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function fieldByName(name) {
    var gpo = gpoRoot();
    if (!gpo) return null;
    var inputs = gpo.querySelectorAll('input, select, textarea');
    for (var i = 0; i < inputs.length; i++) {
      var n = (inputs[i].getAttribute('name') || '').trim();
      if (n.toLowerCase() === name.toLowerCase()) return inputs[i];
    }
    return null;
  }

  function labelFor(input) {
    if (!input) return '';
    if (input.id) {
      var l = document.querySelector('label[for="' + input.id + '"]');
      if (l) return l.textContent.replace(/\s+/g, ' ').trim();
    }
    var wrap = input.closest('.gpo-element, .gpo-field, div');
    var lb = wrap && wrap.querySelector('label');
    return lb ? lb.textContent.replace(/\s+/g, ' ').trim() : (input.getAttribute('name') || 'This field');
  }

  function customSelected(cfg) {
    var want = (cfg.getAttribute('data-custom-value') || 'Custom').toLowerCase();
    var radios = document.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].checked && String(radios[i].value || '').trim().toLowerCase() === want) return true;
    }
    var sels = document.querySelectorAll('select');
    for (var j = 0; j < sels.length; j++) {
      if (String(sels[j].value || '').trim().toLowerCase() === want) return true;
    }
    return false;
  }

  function clearErrors(cfg) {
    var box = cfg.querySelector('[data-sm-error-summary]');
    if (box) { box.textContent = ''; box.hidden = true; }
    document.querySelectorAll('[data-sm-field-error]').forEach(function (n) { n.remove(); });
    document.querySelectorAll('[data-sm-invalid]').forEach(function (n) {
      n.removeAttribute('data-sm-invalid');
      n.removeAttribute('aria-invalid');
      n.removeAttribute('aria-describedby');
    });
  }

  function markInvalid(input, message) {
    if (!input) return;
    input.setAttribute('data-sm-invalid', 'true');
    input.setAttribute('aria-invalid', 'true');
    var id = 'sm-err-' + (input.id || (input.getAttribute('name') || '').replace(/\W+/g, '-'));
    var note = document.createElement('p');
    note.className = 'sm-maison-validation__field-error';
    note.setAttribute('data-sm-field-error', '');
    note.id = id;
    note.textContent = message;
    input.setAttribute('aria-describedby', id);
    if (input.parentNode) input.parentNode.insertBefore(note, input.nextSibling);
  }

  /** Returns { ok, problems: [{input, message}] } */
  function validate(cfg) {
    var required = list(cfg, 'data-required');
    var optional = list(cfg, 'data-optional');
    var problems = [];

    var missingReported = false;
    function reportMissing() {
      if (missingReported) return;
      missingReported = true;
      problems.push({
        input: null,
        message: 'The custom measurement fields have not finished loading. Please reload the page and enter your measurements before adding this piece to your cart.'
      });
    }

    function check(name, isRequired) {
      var input = fieldByName(name);
      // Fail closed: a REQUIRED measurement whose field cannot be found must never pass silently.
      if (!input) { if (isRequired) reportMissing(); return; }
      var raw = String(input.value == null ? '' : input.value).trim();
      var label = labelFor(input);
      if (raw === '') {
        if (isRequired) problems.push({ input: input, message: label + ' is required for a custom size.' });
        return;
      }
      var num = Number(raw);
      if (!isFinite(num)) {
        problems.push({ input: input, message: label + ' must be a number.' });
        return;
      }
      if (num <= 0) {
        problems.push({ input: input, message: label + ' must be greater than zero.' });
      }
    }

    required.forEach(function (n) { check(n, true); });
    optional.forEach(function (n) { check(n, false); });

    return { ok: problems.length === 0, problems: problems };
  }

  function report(cfg, problems) {
    clearErrors(cfg);
    problems.forEach(function (p) { markInvalid(p.input, p.message); });
    var box = cfg.querySelector('[data-sm-error-summary]');
    if (box) {
      var intro = cfg.getAttribute('data-summary-message') || 'Please complete your custom measurements before adding this piece to your cart.';
      box.textContent = intro + ' ' + problems.map(function (p) { return p.message; }).join(' ');
      box.hidden = false;
    }
    var firstWithInput = null;
    for (var i = 0; i < problems.length; i++) {
      if (problems[i].input) { firstWithInput = problems[i].input; break; }
    }
    if (firstWithInput && typeof firstWithInput.focus === 'function') {
      try {
        firstWithInput.focus({ preventScroll: false });
        firstWithInput.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } catch (e) { /* focus is best-effort */ }
    }
  }

  function isAddToCartTrigger(target) {
    if (!target || !target.closest) return false;
    return !!target.closest('add-to-cart-component button, button[name="add"], [ref="addToCartButton"]');
  }

  function guard(event) {
    var cfg = config();
    if (!cfg) return;
    if (!gpoPresent()) return;

    if (event.type === 'click' && !isAddToCartTrigger(event.target)) return;
    if (event.type === 'submit') {
      var form = event.target;
      var action = (form && form.getAttribute && form.getAttribute('action')) || '';
      if (action.indexOf('/cart/add') === -1) return;
    }

    if (!customSelected(cfg)) { clearErrors(cfg); return; }

    var result = validate(cfg);
    if (result.ok) { clearErrors(cfg); return; }

    event.preventDefault();
    event.stopImmediatePropagation();
    report(cfg, result.problems);
  }

  window.addEventListener('click', guard, true);
  window.addEventListener('submit', guard, true);

  // Clear stale errors when the customer changes size away from Custom, or edits a flagged field.
  window.addEventListener('change', function (e) {
    var cfg = config();
    if (!cfg) return;
    if (e.target && e.target.matches && e.target.matches('input[type="radio"], select')) {
      if (!customSelected(cfg)) clearErrors(cfg);
    }
    if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-sm-invalid')) {
      var again = validate(cfg);
      if (again.ok) clearErrors(cfg);
    }
  }, true);

  /* -------------------------------------------------------------------------
   * C-02b. Size-conditional measurement fields (Maison only).
   *
   * The Globo app block renders Bust / Waist / Hips / Shoulder Width / Sleeve
   * Length / Dress Length / Height / Heel Height / Additional Notes on every
   * Maison PDP regardless of the selected Shopify size. Two problems follow:
   * the fields are meaningless for XS / S / M / L, and - measured on the draft
   * theme on 2026-09-13 - whatever is typed into them is still submitted, so a
   * size S add-to-cart carried all eight measurements into the cart as
   * line-item properties.
   *
   * Fix, theme-side only. When Size is not Custom: blank the measurement inputs
   * (Globo omits empty fields from its cart payload - verified on the same
   * draft) and hide the Globo APP BLOCK WRAPPER. That wrapper is Liquid-
   * rendered, so it already exists when this deferred script runs, before Globo
   * mounts its fields into it - the fields therefore never paint and there is
   * no flash. Anything the customer already typed is stashed in memory and put
   * back if they return to Custom in the same page view.
   *
   * Globo's markup, product data and option set are untouched. The wrapper is
   * hidden with an inline style so no stylesheet has to win a specificity
   * fight, and every function below no-ops unless the Maison validation block
   * is on the page - which is how RTW and swimwear stay unaffected, even
   * though they carry the same Globo app block.
   *
   * Size detection deliberately reuses customSelected() - the visibility rule
   * and the validation rule can then never disagree about what "Custom" means.
   * ---------------------------------------------------------------------- */

  var MEASURE_WRAP_SELECTOR = '[id*="globo_product_options"]';
  var stash = Object.create(null);

  function measurementWrap() {
    var direct = document.querySelector(MEASURE_WRAP_SELECTOR);
    if (direct) return direct;
    var inner = document.querySelector(GPO_SELECTOR);
    return inner && inner.closest ? inner.closest('.shopify-app-block') : null;
  }

  function measurementInputs() {
    var root = gpoRoot();
    if (!root) return [];
    return Array.prototype.slice.call(root.querySelectorAll('input, select, textarea'));
  }

  function keyFor(input) {
    return (input.getAttribute('name') || input.id || '').trim();
  }

  function fire(input) {
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function stashAndClear() {
    measurementInputs().forEach(function (input) {
      var key = keyFor(input);
      if (!key) return;
      if (input.type === 'checkbox' || input.type === 'radio') {
        if (input.checked) {
          stash[key] = true;
          input.checked = false;
          fire(input);
        }
        return;
      }
      if (String(input.value == null ? '' : input.value) !== '') {
        stash[key] = input.value;
        input.value = '';
        fire(input);
      }
    });
  }

  function restoreStashed() {
    measurementInputs().forEach(function (input) {
      var key = keyFor(input);
      if (!key || !Object.prototype.hasOwnProperty.call(stash, key)) return;
      if (input.type === 'checkbox' || input.type === 'radio') {
        if (!input.checked) {
          input.checked = true;
          fire(input);
        }
      } else if (String(input.value == null ? '' : input.value) === '') {
        input.value = stash[key];
        fire(input);
      }
      delete stash[key];
    });
  }

  /* -------------------------------------------------------------------------
   * C-02. Required-state labelling.
   *
   * The CRO audit asks for the measurements to be grouped "with units, examples
   * and required-state labels". Globo already supplies the units (the field
   * labels read "Bust (cm)") and the examples (the placeholders), but it renders
   * every input with no `required` attribute and no visual marker, so before
   * submitting there was nothing to tell a customer which six of the eight
   * measurements are actually mandatory - they found out only by being rejected.
   *
   * The required / optional split is NOT invented here: it is read from the same
   * owner-editable block settings the validator already enforces, so the marker
   * and the rule can never disagree.
   *
   * aria-required is set rather than the native `required` attribute on purpose.
   * These inputs sit outside the product <form>, so native validation would do
   * nothing today - but if Globo ever moved them inside it, `required` would let
   * the browser block the submit silently, ahead of this script's own message.
   * aria-required announces the state without touching submission behaviour.
   * ---------------------------------------------------------------------- */

  function labelElementFor(input) {
    if (!input) return null;
    if (input.id) {
      var byFor = document.querySelector('label[for="' + input.id + '"]');
      if (byFor) return byFor;
    }
    var wrap = input.closest('.gpo-element, .gpo-field, div');
    return wrap ? wrap.querySelector('label') : null;
  }

  function markRequiredState() {
    var cfg = config();
    if (!cfg) return;
    var required = list(cfg, 'data-required');
    var flagText = cfg.getAttribute('data-required-flag') || 'Required';
    required.forEach(function (name) {
      var input = fieldByName(name);
      if (!input) return;
      input.setAttribute('aria-required', 'true');
      var label = labelElementFor(input);
      if (!label || label.querySelector('[data-sm-required-flag]')) return;
      var flag = document.createElement('span');
      flag.className = 'sm-required-flag';
      flag.setAttribute('data-sm-required-flag', '');
      flag.textContent = flagText;
      label.appendChild(flag);
    });
  }

  function applySizeVisibility() {
    var cfg = config();
    if (!cfg) return;
    var wrap = measurementWrap();
    if (!wrap) return;
    if (customSelected(cfg)) {
      wrap.style.removeProperty('display');
      wrap.removeAttribute('aria-hidden');
      wrap.removeAttribute('data-sm-measurements-hidden');
      restoreStashed();
      markRequiredState();
    } else {
      stashAndClear();
      wrap.style.setProperty('display', 'none');
      wrap.setAttribute('aria-hidden', 'true');
      wrap.setAttribute('data-sm-measurements-hidden', 'true');
      clearErrors(cfg);
    }
  }

  window.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('input[type="radio"], select')) {
      applySizeVisibility();
    }
  }, true);

  applySizeVisibility();

  // Globo mounts its fields asynchronously. Keep re-applying until they exist,
  // so a value can never be left behind in a field the customer cannot see.
  var settleTicks = 0;
  var settleTimer = setInterval(function () {
    settleTicks += 1;
    applySizeVisibility();
    markRequiredState();
    if (gpoRoot() || settleTicks > 40) clearInterval(settleTimer);
  }, 250);
})();
